<template>
  <div id="dashboard" :class="{'hidden': !showNavigation}">
    <nav class="main-header is-pulled-left">
      <div class="left-item-container">
        <button class="menu-toggle-button" @click="toggleNavigation">
          <img src="../assets/menu-expand.svg" alt="=">
        </button>
        <AddNewItemDropdown></AddNewItemDropdown>
      </div>
      <div class="is-pulled-right">
        <GlobalSearch></GlobalSearch>
        <Notifications></Notifications>
        <UserActions></UserActions>
      </div>
    </nav>
    <nav class="side-navigation" :class="{'active': navEnabled, 'file-container': !!hierarchyParams}">
      <div class="fixed">
        <a class="logo" @click="$router.push({name: !isDocflowHead ? 'dashboard' : 'extendedDashboard'})">
          <img src="../assets/logo.svg" alt="WorkFlow" width="100">
        </a>
        <button class="menu-toggle-button" @click="toggleNavigation">
          <img src="../assets/menu-hide.svg" alt="<=">
        </button>
      </div>
      <template v-if="!hierarchyParams">
        <div v-for="item in navigationItems"
             v-if="item.show"
             :key="item.name"
             :class="{
             'active': isInRoute(item.items ? item.items : item.name),
             'expanded': isInRoute(item.items ? item.items : item.name)
           }">
          <div class="item" @click="!item.items ? goTo(item.name) : expand($event)">
            <i :class="`icon-basic-${item.icon}`"></i>
            {{ $t(`navigation.${item.name}`) }}
            <b-tag v-if="item.tag"
                   class="ml-5 is-small"
                   :type="item.tag.type" rounded>
              {{ item.tag.text }}
            </b-tag>
            <b-icon v-if="item.items && item.items.length > 0"
                    class="chevron"
                    icon="chevron-down"
                    size="is-small"></b-icon>
          </div>
          <div class="sub" v-if="item.items && item.items.length > 0">
            <a v-for="sub in item.items"
               v-if="sub.show"
               :key="sub.name"
               @click.prevent="goTo(sub.name)"
               :class="{active: isInRoute(sub.name)}">
              {{ $t(`navigation.${sub.name}`) }}
              <span v-if="sub.tag" class="tag is-small is-primary">
              {{ sub.tag.text }}
            </span>
            </a>
          </div>
        </div>
      </template>
      <div class="hierarchy" v-else>
        <div class="document" v-if="hierarchy.document">
          <div class="level info">
            <div class="level-left gt">{{ $t(`workflow.states.${hierarchy.document.state}`) }}</div>
            <div class="level-right gt">{{ hierarchy.document.term_date | localedShortDate }}</div>
          </div>
          <div class="level">
            <div class="level-left">
              <div class="title" @click="documentDetail(hierarchy.document.id, hierarchy.document.category)">
                <b-icon icon="clipboard-arrow-down-outline" class="icon" size="is-small"
                        v-if="hierarchy.document.category === 'incoming'"/>
                <b-icon icon="clipboard-arrow-up-outline" class="icon" size="is-small"
                        v-if="hierarchy.document.category === 'outgoing'"/>
                <b-icon icon="clipboard-account-outline" class="icon" size="is-small"
                        v-if="hierarchy.document.category === 'appeal'"/>
                <span class="register-book">{{ hierarchy.document.register_book__name }}</span>
                <span class="name">{{ hierarchy.document.registration_number }}</span>
              </div>
            </div>
            <div class="level-right">
              <a @click="closeHierarchy" class="download-document-file">
                <b-icon size="is-small" icon="close"></b-icon>
              </a>
            </div>
          </div>
          <div class="gt">{{ hierarchy.document.description }}</div>
        </div>
        <div class="assignments" v-if="hierarchyAssignments.length">
          <div class="assignment" v-for="assignment in hierarchyAssignments">
            <div class="level gt">
              <div class="level-left">{{ $t(`workflow.states.${assignment.state}`) }}</div>
              <div class="level-right">{{ assignment.term_date | localedShortDate }}</div>
            </div>
            <div class="title" @click="assignmentDetail(assignment.id)">
              {{ $t('docflow.workflow.title') }} {{ assignment.id }}
            </div>
            <div class="level">
              <div class="level-left">
                <div class="maintainer" v-if="assignment.maintainer">
                  <b-icon icon="shield-account" size="is-small" class="icon"></b-icon>
                  <span>{{ assignment.maintainer.initials }}</span>
                </div>
              </div>
              <div class="level-right" v-if="assignment.tasks.length">
                <a @click="assignment.expanded = !assignment.expanded">
                  <span>{{ assignment.expanded ? $t('interface.hide') : $t('reports.tasks') }}</span>
                  <b-icon class="chevron"
                          :icon="`chevron-${assignment.expanded ? 'down' : 'right'}`"
                          size="is-small"></b-icon>
                </a>
              </div>
            </div>
            <div class="tasks" :class="{expanded: assignment.expanded}">
              <div class="task"
                   v-for="task in assignment.tasks"
                   @click="taskDetail(task.id, assignment.id)">
                <div class="level gt">
                  <div class="level-left">{{ $t(`workflow.states.${task.state}`) }}</div>
                  <div class="level-right">{{ task.term_date | localedShortDate }}</div>
                </div>
                <div class="description">
                  {{ task.description }}
                </div>
                <div class="executors">
                  <div class="maintainer" v-for="executor in task.executors">
                    <span>{{ executor.initials }}</span>
                  </div>
                  <b-icon size="is-small" icon="star" v-if="task.is_executive" class="executive"></b-icon>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
    <template v-if="hierarchy.document && hierarchyParams">
      <div class="navigation-files">
        <div class="note" v-if="hierarchy.can_upload_files">
          {{ $t('interface.dragDropNote') }}
          <div class="control mt-5">
            <b-upload v-model="file" @input="fileUpload">
              <a class="button is-primary is-small">
                <b-icon icon="file-outline" size="is-small"></b-icon>
                <span>{{ $t('interface.uploadFile') }}</span>
              </a>
            </b-upload>
          </div>
        </div>
        <div class="file-item is-clearfix" v-for="file in hierarchyFiles" :class="{general: file.general}">
          <div class="ico">
            <div :class="`file-type-ico ${file.extension}`"></div>
          </div>
          <div class="info">
            <div class="level">
              <a class="level-left name" :href="file.file" :title="file.name">{{ file.name }}</a>
              <div class="level-right">{{ file.created_at | localedShortDate }}</div>
            </div>
            <div class="level mt-7">
              <div class="level-left">{{ file.author.initials }}</div>
              <div class="level-right">
                <a @click="removeFile(file)" target="_blank" v-if="account.employee.id === file.author.id">
                  <b-icon icon="delete" size="is-small"></b-icon>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="fileUploader" v-if="hierarchy.can_upload_files">
        <div class="note">{{ $t('interface.dragDropFiles') }}</div>
      </div>
    </template>
    <div class="main-content">
      <section class="section">
        <div class="level" v-if="breadcrumbs">
          <div class="level-left">
            <nav class="breadcrumb" aria-label="breadcrumbs">
              <ul>
                <li>
                  <a @click="goTo('dashboard')">
                    {{ $t(`routes.dashboard`) }}
                  </a>
                </li>
                <template v-for="breadcrumb in breadcrumbs">
                  <li :class="{
                    'is-active': breadcrumb.name === currentRoute,
                    'passive': !('path' in breadcrumb)
                  }">
                    <a @click="goTo(breadcrumb.path, true)">
                      {{ $t(`routes.${breadcrumb.name}`) }}
                    </a>
                  </li>
                </template>
              </ul>
            </nav>
          </div>
          <div class="level-right breadcrumb-navigation">
            <a class="back" @click="$router.go(-1)">
              <b-icon icon="keyboard-backspace" size="is-small"></b-icon>
              {{ $t('interface.historyBack') }}
            </a>
          </div>
        </div>
        <slot></slot>
        <Loading :show="loading"></Loading>
      </section>
    </div>
    <CommentsWindow @update="loadHierarchy" v-if="hierarchy.document && hierarchyParams" ref="comments"/>
    <div class="network-error" v-if="showNetworkError">
      <div class="error-message">
        <b-icon icon="alert-outline" class="x-large icon"></b-icon>
        <h1 class="title is-4">{{ $t('interface.noInternet') }}</h1>
        <div class="note">{{ $t('interface.checkInternet') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters, mapState} from 'vuex'
  import GlobalSearch from './GlobalSearch'
  import Loading from './Loading'
  import Notifications from './Notifications'
  import UserActions from './UserActions'
  import AddNewItemDropdown from './AddNewItemDropdown'
  import CommentsWindow from './CommentsWindow'
  import {documentDetail, assignmentDetail, taskDetail} from '@/utils'

  export default {
    props: {
      breadcrumbs: {
        type: null,
        default: () => []
      },
      hierarchyParams: {
        type: null,
        default: false
      }
    },
    components: {
      GlobalSearch,
      Notifications,
      AddNewItemDropdown,
      UserActions,
      Loading,
      CommentsWindow
    },
    data() {
      return {
        showNetworkError: false,
        timer: null,
        navEnabled: false,
        badges: {
          assignments: 0,
          service: {
            issues: 0,
            surveys: 0,
            announcements: 0,
            total: 0
          },
          documents: {
            incoming: 0,
            outgoing: 0,
            appeals: 0,
            for_agreement: 0,
            for_registration: 0,
            total: 0
          }
        },
        hierarchyAssignments: [],
        file: null
      }
    },
    methods: {
      toggleNavigation() {
        this.$store.commit('Account/SET_NAVIGATION_VISIBILITY', !this.showNavigation)
      },
      expand(e) {
        e.target.parentNode.classList.toggle('expanded')
      },
      goTo(route, breadcrumb = false) {
        if (!breadcrumb) this.$store.commit('Filters/RESET');
        if (typeof route === "string") {
          this.$router.push({name: route})
        } else if (typeof route === "object") {
          this.$router.push(route)
        }
      },
      documentDetail,
      assignmentDetail,
      taskDetail,
      isInRoute(name) {
        if (this.breadcrumbs) {
          let breadcrumbs = this.breadcrumbs.map(breadcrumb => breadcrumb.name)
          if (typeof name === 'string') {
            return breadcrumbs.includes(name)
          } else {
            for (let i = 0; i < name.length; i++) {
              if (breadcrumbs.includes(name[i].name)) return true
            }
            return false
          }
        } else return false
      },
      loadUnreadCounters() {
        this.$axios.get('account/badges/', {__silent: true}).then((response) => {
          this.badges = response.data
        })
      },
      loadHierarchy() {
        if (this.hierarchyParams) {
          this.$store.dispatch('Docflow/getHierarchy', this.hierarchyParams).then(() => {
            this.hierarchyAssignments = this.hierarchy.document ? this.hierarchy.document.assignments
              : [this.hierarchy.assignment]
            this.hierarchyAssignments = this.hierarchyAssignments.map(assignment => ({
              ...assignment,
              expanded: false
            }))
            if (this.hierarchyAssignments.length) this.hierarchyAssignments[0].expanded = true
            this.hierarchyAssignments = this.hierarchyAssignments.map(assignment => ({
              ...assignment,
              tasks: assignment.tasks.sort((a, b) => (a.is_executive === b.is_executive) ? 0 : a.is_executive ? -1 : 1)
            }))
          })
        }
      },
      fileUpload() {
        this.$store.dispatch('Docflow/uploadFile', {
          file: this.file,
          id: this.hierarchy.document.id
        }).then(this.loadHierarchy)
      },
      removeFile(file) {
        this.$dialog.confirm({
          message: this.$t('interface.fileDeletingAcceptation'),
          cancelText: this.$t('interface.cancel'),
          confirmText: 'OK',
          type: 'is-danger',
          onConfirm: () => {
            if (file.source === 'document') {
              this.$store.dispatch('Docflow/removeFile', {
                fileId: file.id,
                documentId: this.hierarchy.document.id
              }).then(() => {
                this.$toast.open(this.$t('interface.fileSuccessfulDeleted'))
                this.loadHierarchy()
              })
            }
          }
        })
      },
      closeHierarchy() {
        switch (this.hierarchy.document.category) {
          case 'incoming':
            this.goTo('docflowIncoming');
            break;
          case 'outgoing':
            this.goTo('docflowOutgoing');
            break;
          default:
            this.goTo('docflowAppeals');
            break;
        }
      }
    },
    mounted() {
      this.loadHierarchy()
      if (this.hierarchyParams && this.hierarchy.document) {
        let fileUploader = document.getElementById('fileUploader')
        ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
          fileUploader.addEventListener(eventName, e => {
            e.preventDefault()
            e.stopPropagation()
          }, false)
        })
        ;['dragenter', 'dragover'].forEach(eventName => {
          document.addEventListener(eventName, () => fileUploader.classList.add('highlight'), false)
        })
        ;['dragleave', 'drop'].forEach(eventName => {
          fileUploader.addEventListener(eventName, () => fileUploader.classList.remove('highlight'), false)
        })
        fileUploader.addEventListener('drop', e => {
          const file = e.dataTransfer.files[0]
          this.$dialog.confirm({
            title: this.$t('interface.confirm'),
            message: this.$t('interface.confirmFileUpload'),
            confirmText: this.$t('interface.accept'),
            cancelText: this.$t('interface.cancel'),
            type: 'is-info',
            hasIcon: true,
            onConfirm: () => {
              this.$store.dispatch('Docflow/uploadFile', {
                file,
                id: this.hierarchy.document.id
              }).then(this.loadHierarchy)
            }
          })
        }, false)
      }
    },
    computed: {
      ...mapState('Account', ['account', 'showNavigation']),
      ...mapState(['error', 'networkError', 'loading']),
      ...mapState('Docflow', ['hierarchy']),
      ...mapGetters('Account', ['isProjectManager', 'isDocflowManager', 'isDocflowModerator', 'isDocflowHead']),
      navigationItems() {
        return [
          {name: 'dashboard', icon: 'webpage-img-txt', show: !this.isDocflowHead},
          {name: 'extendedDashboard', icon: 'webpage-img-txt', show: this.isDocflowHead},
          {
            name: 'docflow', icon: 'archive-full', show: true,
            tag: this.badges.documents.total > 0
              ? {type: 'is-danger', text: this.badges.documents.total} : false,
            items: [
              {
                name: 'docflowIncoming', show: true,
                tag: this.badges.documents.incoming > 0
                  ? {type: 'is-danger', text: this.badges.documents.incoming} : false
              },
              {
                name: 'docflowOutgoing', show: true,
                tag: this.badges.documents.outgoing > 0
                  ? {type: 'is-danger', text: this.badges.documents.outgoing} : false
              },
              {
                name: 'docflowAppeals', show: true,
                tag: this.badges.documents.appeals > 0
                  ? {type: 'is-danger', text: this.badges.documents.appeals} : false
              },
              {
                name: 'docflowAgreementsList', show: true,
                tag: this.badges.documents.for_agreement > 0
                  ? {type: 'is-danger', text: this.badges.documents.for_agreement} : false
              },
              {
                name: 'docflowRegistrationsList', show: this.isDocflowManager,
                tag: this.badges.documents.for_registration > 0
                  ? {type: 'is-danger', text: this.badges.documents.for_registration} : false
              },
            ]
          },
          {
            name: 'documents', icon: 'todo-txt', show: true,
            tag: this.badges.assignments > 0
              ? {type: 'is-danger', text: this.badges.assignments} : false
          },
          /*{name: 'projects', icon: 'archive-full', show: this.isProjectManager},*/
          {name: 'departments', icon: 'share', show: true},
          {
            name: 'service', icon: 'pencil-ruler-pen', show: true,
            tag: this.badges.service.total > 0
              ? {type: 'is-danger', text: this.badges.service.total} : false,
            items: [
              {name: 'globalSearch', show: true},
              {
                name: 'issues', icon: 'postcard-multiple', show: true,
                tag: this.badges.service.issues > 0
                  ? {type: 'is-danger', text: this.badges.service.issues} : false
              },
              {
                name: 'surveys', icon: 'mixer2', show: true,
                tag: this.badges.service.surveys > 0
                  ? {type: 'is-danger', text: this.badges.service.surveys} : false
              },
              {name: 'resources', icon: 'usb', show: true},
              {
                name: 'announcements', icon: 'postcard', show: true,
                tag: this.badges.service.announcements > 0
                  ? {type: 'is-danger', text: this.badges.service.announcements} : false
              },
            ]
          },
          {name: 'reportsList', icon: 'spread-text', show: true},
          {name: 'analyticsDashboard', icon: 'display', show: true},
          {
            name: 'admin', icon: 'gear', show: this.isDocflowModerator,
            items: [
              {name: 'employees', show: true},
              {name: 'manageDepartments', show: true},
              {name: 'externalOrganizations', show: true},
              {name: 'positions', show: true},
              {name: 'postNames', show: true},
              {name: 'registerBooks', show: true},
              {name: 'documentTypes', show: true},
              {name: 'deliveryTypes', show: true},
              {name: 'issueCategories', show: true},
              {name: 'resourceCategories', show: true},
              {name: 'manageResources', show: true},
            ]
          }
        ]
      },
      hierarchyFiles() {
        let files = this.hierarchy.files
        if (this.hierarchy.document && this.hierarchy.document.file)
          files.unshift({
            author: {
              initials: this.$t('documents.document')
            },
            created_at: this.hierarchy.document.term_date,
            extension: this.hierarchy.document.file.split('.').pop(),
            file: this.hierarchy.document.file,
            name: this.$t('interface.generalDocument'),
            general: true
          })
        return files
      },
      currentRoute() {
        return this.$route.name
      },
      isPreviousPage() {
        return document.referrer === ""
      }
    },
    watch: {
      error(error) {
        if (error) {
          this.$toast.open({
            message: error,
            type: 'is-danger',
            duration: 6000
          })
          this.$store.commit('ERROR', false)
        }
      },
      networkError(error) {
        if (error) {
          this.showNetworkError = true
          let checkOnlineInterval = setInterval(() => {
            if (navigator.onLine) {
              this.$store.commit('NETWORK_ERROR', false)
              clearInterval(checkOnlineInterval)
              location.reload(false)
            }
          }, 5000)
        }
      }
    },
    created() {
      this.loadUnreadCounters()
      this.timer = setInterval(() => {
        this.loadUnreadCounters()
      }, 15 * 1000)
    },
    destroyed() {
      clearInterval(this.timer)
    }
  }
</script>

<style lang="scss">
  @import "../assets/variables";

  #dashboard {
    &.hidden {
      .side-navigation,
      .navigation-files {
        left: -$navigation-width;
      }

      .main-header {
        left: 0;

        .menu-toggle-button {
          width: 54px;
        }
      }

      .main-content {
        margin-left: 0;
      }
    }

    .main-header {
      position: fixed;
      top: 0;
      left: $navigation-width;
      right: 0;
      height: calc(#{$header-height} + 1px);
      background: #fff;
      border-bottom: 1px solid #E6E8F1;
      z-index: 2;
      transition: all .2s ease;

      .header-item {
        border-right: 1px solid $border-color;
        display: inline-block;
        height: $header-height;
        vertical-align: top;

        &:last-child {
          border: 0;
        }
      }

      .left-item-container {
        display: inline-block;
        height: $header-height;
        vertical-align: top;
        margin-left: 54px;
      }

      .menu-toggle-button {
        background: transparent;
        border: 0;
        height: $header-height;
        line-height: $header-height;
        overflow: hidden;
        padding: 0;
        width: 0;
        transition: all .2s ease;
        text-align: left;
        cursor: pointer;

        img {
          width: 20px;
          height: auto;
        }
      }

      .search {
        input {
          margin: 0;
          border: 0;
          height: calc(#{$header-height});
          padding: 0 16px;
          color: #757575;
          font-size: 15px;
          width: 360px;

          &::placeholder {
            color: #C3C3C3;
            opacity: 1;
          }

          &:-ms-input-placeholder {
            color: #C3C3C3;
          }

          &::-ms-input-placeholder {
            color: #C3C3C3;
          }
        }

        img {
          vertical-align: sub;
          position: relative;
          top: 3px;
        }
      }
    }

    .navigation-files {
      padding: 20px 15px 20px 30px;
      position: fixed;
      bottom: 0;
      height: 200px;
      left: 0;
      width: $navigation-width;
      z-index: 2;
      overflow: scroll;
      overflow-x: hidden;
      transition: all .2s ease;
      font-size: 13px;

      &::-webkit-scrollbar-track {
        background-color: $border-color;
      }

      &::-webkit-scrollbar {
        width: 1px;
        background-color: $border-color;
        border-radius: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #a0a0a0;
        border-radius: 10px;
      }

      .note {
        color: #9D9D9D;
        margin-bottom: 15px;
      }

      .file-item {
        &:not(:last-child) {
          margin-bottom: 10px;
        }

        .ico {
          width: 33px;
          position: relative;
          left: -5px;
          float: left;
        }

        .info {
          color: #9D9D9D;

          .level {
            margin-bottom: 2px;
            line-height: 80%;
          }

          .name {
            font-size: 14px;
            color: #444444;
            max-width: 115px;
            height: 15px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        &.general {
          .name {
            font-weight: 500;
          }
        }
      }
    }

    .side-navigation {
      padding: 70px 0 8px;
      position: fixed;
      top: 0;
      height: 100%;
      left: 0;
      width: $navigation-width;
      z-index: 2;
      overflow: scroll;
      overflow-x: hidden;
      transition: all .2s ease;

      &.file-container {
        height: calc(100% - 200px);
        padding-bottom: 0;
      }

      &::-webkit-scrollbar-track {
        background-color: $border-color;
      }

      &::-webkit-scrollbar {
        width: 1px;
        background-color: $border-color;
        border-radius: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #a0a0a0;
        border-radius: 10px;
      }

      .fixed {
        position: fixed;
        top: 0;
        background: #fff;
        width: $navigation-width;
        z-index: 9;

        .logo {
          position: relative;
          top: -1px;
          display: inline-block;
          height: $header-height;
          line-height: $header-height;
          padding: 6px 28px 0;
          margin: 0;
        }

        .menu-toggle-button {
          margin-top: 25px;
          margin-left: 42px;
          vertical-align: top;
          background: transparent;
          border: 0;
          cursor: pointer;

          img {
            width: 26px;
            height: auto;
          }
        }
      }

      & > div {
        & > .item {
          position: relative;
          font-size: 14px;
          color: #727272;
          display: block;
          width: 100%;
          cursor: pointer;
          padding: 0 27px;
          height: 55px;
          line-height: 55px;

          & > i {
            font-size: 22px;
            vertical-align: middle;
            margin-right: 10px;
          }

          .chevron {
            position: absolute;
            z-index: -1;
            right: 16px;
            top: 20px;
            transition: all .2s ease;
          }
        }

        &.active > .item, &:hover > .item {
          color: #3498db;
        }

        &.active > .item:before {
          display: block;
          content: '';
          height: 27px;
          width: 4px;
          position: absolute;
          right: -1px;
          top: 16px;
          background: #3498db;
        }

        & > .sub {
          background: #ebecf0;
          padding: 0;
          max-height: 0;
          overflow: hidden;
          transition: max-height .4s ease-in-out;

          a {
            display: block;
            width: 100%;
            padding: 10px 42px;

            &.active {
              color: #3498db;
            }
          }
        }

        &.expanded > .sub {
          max-height: 530px;
        }

        &.expanded .chevron {
          transform: rotate(180deg);
          transform-origin: center;
        }
      }

      .hierarchy {
        font-size: 13px;

        .gt {
          color: #929292;
        }

        .document {
          padding-left: 30px;
          padding-right: 15px;
          margin-bottom: 12px;

          .level {
            margin-bottom: 0;
          }

          .info {
            margin-bottom: 3px;
          }

          .title {
            margin-bottom: 6px;
            font-size: 15.5px;
            font-weight: bold;
            display: inline-block;
            cursor: pointer;

            .icon {
              margin-right: 4px;
              font-size: 18px;
            }

            .register-book {
              display: inline-block;
              background: #0f81cc;
              color: #fff;
              text-transform: uppercase;
              font-size: 9.5px;
              height: 14px;
              line-height: 15px;
              font-weight: 900;
              border-radius: 3px;
              padding: 0 4px;
              margin-right: 4px;
            }

            .name {
              display: inline-block;
              vertical-align: middle;
              position: relative;
              top: -1px;
            }
          }

          .download-document-file {
            color: #0f81cc;
            font-size: 15px;
            position: relative;
            top: -3px;
          }
        }

        .assignments {
          background: #EBECF0;
          padding: 12px 15px 12px 30px;

          .level {
            margin-bottom: 3px;
          }

          .title {
            font-size: 14px;
            margin-bottom: 8px;
            color: #727272;
            cursor: pointer;
            display: inline-block;
          }

          .executive {
            color: #0f81cc;
            position: relative;
            top: -2px;
          }

          .maintainer {
            background: #fff;
            border-radius: 4px;
            color: #727272;
            height: 22px;
            line-height: 22px;
            padding: 0 7px;
            display: inline-block;

            &:not(:last-child) {
              margin-right: 5px;
            }

            .icon {
              color: #73828B;
              font-size: 16px;
              vertical-align: center;
              position: relative;
              top: -2px;
              margin-right: 5px;
            }
          }

          a {
            color: #3498DB;

            .chevron {
              position: relative;
              top: -1px;
            }
          }

          .tasks {
            max-height: 0;
            overflow: hidden;
            transition: all .5s;
          }

          .tasks.expanded {
            max-height: 3000px;
          }

          .task {
            background: #fff;
            border-radius: 5px;
            padding: 7px 12px;
            margin-top: 4px;
            cursor: pointer;

            .maintainer {
              background: #EBECF0;
            }

            .description {
              font-size: 14px;
              margin-bottom: 4px;
            }
          }
        }
      }
    }

    .main-content {
      margin-top: $header-height;
      margin-left: $navigation-width;
      position: relative;
      transition: all .2s ease;
    }

    .back {
      img {
        vertical-align: middle;
        position: relative;
        top: -2px;
        margin-right: 5px;
      }
    }
  }

  .network-error {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(271, 76, 60, 0.85);
    z-index: 999;

    .error-message {
      display: inline-block;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;

      * {
        color: #fff !important;
      }

      .icon {
        margin-bottom: 72px;
      }

      h1 {
        margin-bottom: 5px;
      }

      .note {
        font-size: 16px;
      }
    }
  }

  #fileUploader {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, .6);
    opacity: 0;
    z-index: -1;

    &.highlight {
      opacity: 1;
      z-index: 999999;
    }

    .note {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #fff;
      padding: 20px 30px;
      font-size: 18px;
    }
  }
</style>
